import { UIControls, KeyboardState } from '../types';
import { HDRI_MAPS } from '../utils';
import { t, i18n } from '../i18n/translations';
import { showCursorSpinner, hideCursorSpinner } from '../utils/cursorSpinner';
import { authService } from '../auth/AuthService';
import { getDemoRestrictionMessage, isDemoMode } from '../utils/demoMode';

const LOADING_OVERLAY_MIN_DISPLAY_MS = 5000;

export class UIManager {
  private keyboardState: KeyboardState = {
    w: false,
    a: false,
    s: false,
    d: false,
    q: false,
    e: false
  };

  private uiControls: UIControls;
  private loadingOverlayShownAt: number | null = null;
  private loadingOverlayIsInitial: boolean = false;

  constructor() {
    this.uiControls = this.initializeUIControls();
    this.setupEventListeners();
    this.initializeCustomDropdowns();
    // Обновляем опции HDRI и aspect ratio при смене языка
    i18n.onChange(() => {
      const envSelect = document.getElementById('env-select') as HTMLSelectElement;
      if (envSelect) {
        const currentValue = envSelect.value;
        this.populateHDRIOptions(envSelect);
        envSelect.value = currentValue;
        this.updateCustomDropdown('env-select', currentValue);
      }
      
      const aspectRatioSelect = document.getElementById('aspect-ratio-select') as HTMLSelectElement;
      if (aspectRatioSelect) {
        const currentValue = aspectRatioSelect.value;
        this.updateCustomDropdown('aspect-ratio-select', currentValue);
        
        // Обновляем все элементы меню
        const menu = document.getElementById('aspect-ratio-select-menu');
        if (menu) {
          menu.querySelectorAll('.custom-dropdown-item span[data-i18n]').forEach(span => {
            const i18nKey = span.getAttribute('data-i18n');
            if (i18nKey) {
              span.textContent = t(i18nKey);
            }
          });
        }
      }
    });
  }

  private initializeUIControls(): UIControls {
    return {
      color: document.getElementById('u-color') as HTMLInputElement,
      tiling: document.getElementById('u-tiling') as HTMLInputElement,
      rot: document.getElementById('u-rot') as HTMLInputElement,
      env: document.getElementById('u-env') as HTMLInputElement,
      fov: document.getElementById('u-fov') as HTMLInputElement,
      rough: document.getElementById('u-rough') as HTMLInputElement,
      roughTex: document.getElementById('u-rough-tex') as HTMLInputElement,
      metal: document.getElementById('u-metal') as HTMLInputElement,
      norm: document.getElementById('u-norm') as HTMLInputElement
    };
  }

  /**
   * Создает содержимое SVG иконки для окружения
   */
  private populateEnvironmentIcon(icon: SVGElement, environmentValue: string): void {
    // Уникальные иконки для каждого типа окружения
    if (environmentValue === 'none') {
      // Иконка для "Без окружения" - перечеркнутый круг
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '12');
      circle.setAttribute('r', '10');
      icon.appendChild(circle);
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', '4');
      line.setAttribute('y1', '4');
      line.setAttribute('x2', '20');
      line.setAttribute('y2', '20');
      icon.appendChild(line);
    } else if (environmentValue === 'night') {
      // Иконка для "Ночная сцена" - луна и звезды
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z');
      icon.appendChild(path);
      // Звезды
      const star1 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      star1.setAttribute('cx', '18');
      star1.setAttribute('cy', '6');
      star1.setAttribute('r', '1');
      star1.setAttribute('fill', 'currentColor');
      icon.appendChild(star1);
      const star2 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      star2.setAttribute('cx', '22');
      star2.setAttribute('cy', '9');
      star2.setAttribute('r', '1');
      star2.setAttribute('fill', 'currentColor');
      icon.appendChild(star2);
    } else if (environmentValue === 'photostudio') {
      // Иконка для "Фотостудия" - софтбокс/студийный свет
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', '4');
      rect.setAttribute('y', '2');
      rect.setAttribute('width', '16');
      rect.setAttribute('height', '10');
      rect.setAttribute('rx', '2');
      icon.appendChild(rect);
      const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line1.setAttribute('x1', '8');
      line1.setAttribute('y1', '12');
      line1.setAttribute('x2', '12');
      line1.setAttribute('y2', '22');
      icon.appendChild(line1);
      const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line2.setAttribute('x1', '16');
      line2.setAttribute('y1', '12');
      line2.setAttribute('x2', '12');
      line2.setAttribute('y2', '22');
      icon.appendChild(line2);
    } else if (environmentValue === 'daylight') {
      // Иконка для "Дневной свет" - солнце
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '12');
      circle.setAttribute('r', '5');
      icon.appendChild(circle);
      // Лучи солнца
      const rays = [
        { x1: '12', y1: '1', x2: '12', y2: '3' },
        { x1: '12', y1: '21', x2: '12', y2: '23' },
        { x1: '4.22', y1: '4.22', x2: '5.64', y2: '5.64' },
        { x1: '18.36', y1: '18.36', x2: '19.78', y2: '19.78' },
        { x1: '1', y1: '12', x2: '3', y2: '12' },
        { x1: '21', y1: '12', x2: '23', y2: '12' },
        { x1: '4.22', y1: '19.78', x2: '5.64', y2: '18.36' },
        { x1: '18.36', y1: '5.64', x2: '19.78', y2: '4.22' }
      ];
      rays.forEach(ray => {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', ray.x1);
        line.setAttribute('y1', ray.y1);
        line.setAttribute('x2', ray.x2);
        line.setAttribute('y2', ray.y2);
        icon.appendChild(line);
      });
    } else {
      // Иконка по умолчанию - сфера с градиентом
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '12');
      circle.setAttribute('cy', '12');
      circle.setAttribute('r', '10');
      icon.appendChild(circle);
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z');
      icon.appendChild(path);
    }
  }

  private setupEventListeners(): void {
    // Keyboard events - WASD camera movement disabled
    // window.addEventListener('keydown', (e) => {
    //   const key = e.key.toLowerCase();
    //   if (key in this.keyboardState) {
    //     this.keyboardState[key as keyof KeyboardState] = true;
    //     e.preventDefault();
    //   }
    // });

    // window.addEventListener('keyup', (e) => {
    //   const key = e.key.toLowerCase();
    //   if (key in this.keyboardState) {
    //     this.keyboardState[key as keyof KeyboardState] = false;
    //     e.preventDefault();
    //   }
    // });

    // Model scale - removed

    // Environment select (скрытый, для обратной совместимости)
    const envSelect = document.getElementById('env-select') as HTMLSelectElement;
    if (envSelect) {
      // Заполняем опции HDRI динамически из HDRI_MAPS
      this.populateHDRIOptions(envSelect);
      
      envSelect.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        window.dispatchEvent(new CustomEvent('environmentChange', { detail: target.value }));
      });
    }

    // Aspect ratio select (скрытый, для обратной совместимости)
    const aspectRatioSelect = document.getElementById('aspect-ratio-select') as HTMLSelectElement;
    if (aspectRatioSelect) {
      aspectRatioSelect.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        window.dispatchEvent(new CustomEvent('aspectRatioChange', { detail: target.value }));
      });
    }

    // Camera FOV
    const fovInput = document.getElementById('u-fov') as HTMLInputElement;
    const fovValue = document.getElementById('u-fov-value');
    const cinemaFovSlider = document.getElementById('cinema-fov-slider') as HTMLInputElement;
    const cinemaFovValue = document.getElementById('cinema-fov-value');
    
    const updateCinemaFovFill = () => {
      if (!cinemaFovSlider) return;
      const min = parseFloat(cinemaFovSlider.min) || 20;
      const max = parseFloat(cinemaFovSlider.max) || 90;
      const val = parseFloat(cinemaFovSlider.value);
      const percent = ((val - min) / (max - min)) * 100;
      cinemaFovSlider.style.setProperty('--slider-value', percent + '%');
    };
    
    if (fovInput && fovValue) {
      const syncFov = () => {
        const val = Number(fovInput.value).toFixed(0);
        fovValue.textContent = `${val}°`;
        // Sync cinema FOV slider
        if (cinemaFovSlider) {
          cinemaFovSlider.value = fovInput.value;
          updateCinemaFovFill();
        }
        if (cinemaFovValue) {
          cinemaFovValue.textContent = val;
        }
      };
      syncFov();
      fovInput.addEventListener('input', () => {
        syncFov();
        const value = Number(fovInput.value);
        window.dispatchEvent(new CustomEvent('cameraFovChange', { detail: value }));
      });
    }

    // Background color
    const bgInput = document.getElementById('bg-color') as HTMLInputElement;
    if (bgInput) {
      bgInput.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        window.dispatchEvent(new CustomEvent('backgroundChange', { detail: target.value }));
      });
    }

    // Model upload
    const modelUpload = document.getElementById('model-upload') as HTMLInputElement;
    if (modelUpload) {
      modelUpload.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file) {
          if (isDemoMode()) {
            target.value = '';
            window.dispatchEvent(new CustomEvent('showToolbarMessage', {
              detail: { text: getDemoRestrictionMessage('load-model'), duration: 2500 }
            }));
            return;
          }
          if (!authService.isAdmin()) {
            // UX: reset input value so the same file can be picked later (by admin)
            target.value = '';
            window.dispatchEvent(new CustomEvent('showToolbarMessage', {
              detail: { text: 'Загрузить модель может только администратор', duration: 2500 }
            }));
            return;
          }
          window.dispatchEvent(new CustomEvent('modelUpload', { detail: file }));
        }
      });
    }

    // Save project
    const saveProject = document.getElementById('save-project') as HTMLButtonElement;
    if (saveProject) {
      saveProject.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('saveProject'));
      });
    }

    // Screenshot
    const screenshotBtn = document.getElementById('screenshot-btn') as HTMLButtonElement;
    if (screenshotBtn) {
      screenshotBtn.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('takeScreenshot'));
      });
    }
    
    // Toggle UV view - removed

    // Print upload
    this.setupPrintUpload();

    // Reset camera обработан в uiInit.ts (чтобы избежать дублирования логики/событий)

    // Print elevation slider
    const printOffsetEl = document.getElementById('print-offset') as HTMLInputElement;
    const printOffsetVal = document.getElementById('print-offset-value') as HTMLElement;
    if (printOffsetEl && printOffsetVal) {
      printOffsetEl.addEventListener('input', () => {
        const value = parseFloat(printOffsetEl.value);
        printOffsetVal.textContent = value.toFixed(3) + ' m';
        window.dispatchEvent(new CustomEvent('printOffsetChange', { detail: value }));
      });
    }

    // Camera animation panel
    const camStatus = document.getElementById('cam-pose-status');
    const setStatus = (text: string) => { if (camStatus) camStatus.textContent = text; };
    const updateStatus = (hasStart: boolean, hasEnd: boolean) => {
      if (!camStatus) return;
      if (hasStart && hasEnd) setStatus(t('camera.points.ready'));
      else if (hasStart) setStatus(t('camera.points.start'));
      else if (hasEnd) setStatus(t('camera.points.end'));
      else setStatus(t('camera.points.none'));
    };

    const startBtn = document.getElementById('cam-pose-start');
    const endBtn = document.getElementById('cam-pose-end');
    const saveBtn = document.getElementById('cam-pose-save');
    const arcSelect = document.getElementById('cam-arc-mode') as HTMLSelectElement | null;

    if (startBtn) {
      startBtn.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('cameraPoseStart'));
        (startBtn as any)._hasPose = true;
        updateStatus(true, !!(endBtn as any)?._hasPose);
      });
    }
    if (endBtn) {
      endBtn.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('cameraPoseEnd'));
        (endBtn as any)._hasPose = true;
        updateStatus(!!(startBtn as any)?._hasPose, true);
      });
    }
    // Обработчик playBtn удален - он теперь находится в uiInit.ts
    // где правильно обрабатывается логика play/stop
    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('cameraAnimSave'));
      });
    }
    if (arcSelect) {
      arcSelect.addEventListener('change', () => {
        window.dispatchEvent(new CustomEvent('cameraArcModeChange', { detail: arcSelect.value }));
      });
    }
  }

  private setupPrintUpload(): void {
    // Кнопка графического редактора в layers modal
    setTimeout(() => {
      const layersModal = document.querySelector('#layers-modal .modal-body');
      if (layersModal) {
        const editorBtn = document.createElement('button');
        editorBtn.textContent = '✨ Создать принт';
        editorBtn.title = 'Создайте дизайн в графическом редакторе';
        editorBtn.className = 'w-full px-2 py-1.5 rounded-md bg-gradient-to-r from-cyan-400 to-blue-500 text-black font-semibold hover:scale-105 transition-all mt-2 text-xs';
        editorBtn.onclick = () => {
          window.dispatchEvent(new CustomEvent('openGraphicEditor'));
        };
        
        layersModal.appendChild(editorBtn);
        
        // Кнопка редактирования принта (показывается только для принтов)
        const editPrintBtn = document.createElement('button');
        editPrintBtn.id = 'edit-print-btn';
        editPrintBtn.textContent = '✏️ Редактировать принт';
        editPrintBtn.title = 'Редактировать выбранный принт в графическом редакторе';
        editPrintBtn.className = 'w-full px-2 py-1.5 rounded-md bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold hover:scale-105 transition-all mt-1 text-xs';
        editPrintBtn.style.display = 'none';
        editPrintBtn.onclick = () => {
          window.dispatchEvent(new CustomEvent('editPrint'));
        };
        
        layersModal.appendChild(editPrintBtn);
      }
    }, 100);
  }


  public getUIControls(): UIControls {
    return this.uiControls;
  }

  public getKeyboardState(): KeyboardState {
    return this.keyboardState;
  }

  public showLoader(text?: string): void {
    // Используем loading-overlay вместо старого loader
    this.showLoadingOverlay(text || t('message.loading'));
  }

  public hideLoader(): void {
    // Используем loading-overlay вместо старого loader
    this.hideLoadingOverlay();
  }

  private loadingOverlayHideTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Показывает блокирующий overlay с текстом и процентами.
   * isInitialLoad === true — только при первой загрузке приложения (показывается лого-анимация).
   */
  public showLoadingOverlay(text: string, progress?: number, isInitialLoad?: boolean): void {
    const overlay = document.getElementById('loading-overlay');
    const textEl = document.getElementById('loading-overlay-text');
    const progressEl = document.getElementById('loading-overlay-progress');
    const cancelBtn = document.getElementById('loading-overlay-cancel');
    const actionBtn = document.getElementById('loading-overlay-action');
    
    if (this.loadingOverlayHideTimeout != null) {
      clearTimeout(this.loadingOverlayHideTimeout);
      this.loadingOverlayHideTimeout = null;
    }
    
    if (overlay) {
      this.loadingOverlayShownAt = Date.now();
      this.loadingOverlayIsInitial = isInitialLoad === true;
      overlay.classList.remove('hidden');
      overlay.classList.remove('loading-overlay-fade-out');
      if (this.loadingOverlayIsInitial) {
        overlay.classList.add('loading-overlay-initial');
      } else {
        overlay.classList.remove('loading-overlay-initial');
      }
    }

    // По умолчанию кнопки действий скрыты: показываем их явно через события
    cancelBtn?.classList.add('hidden');
    actionBtn?.classList.add('hidden');
    
    if (textEl) {
      textEl.textContent = text;
    }
    
    if (progressEl) {
      if (progress !== undefined) {
        progressEl.textContent = `${Math.round(progress)}%`;
      } else {
        progressEl.textContent = '';
      }
    }
  }

  /**
   * Обновляет текст и прогресс в overlay
   */
  public updateLoadingOverlay(text?: string, progress?: number, eta?: string, warning?: string): void {
    const textEl = document.getElementById('loading-overlay-text');
    const progressEl = document.getElementById('loading-overlay-progress');
    const etaEl = document.getElementById('loading-overlay-eta');
    const warningEl = document.getElementById('loading-overlay-warning');
    
    if (text && textEl) {
      textEl.textContent = text;
    }
    
    if (progressEl) {
      if (progress !== undefined) {
        progressEl.textContent = `${Math.round(progress)}%`;
      } else {
        progressEl.textContent = '';
      }
    }
    
    // ETA (примерное время)
    if (etaEl) {
      if (eta) {
        etaEl.textContent = `${t('loading.video.eta')} ${eta}`;
        etaEl.classList.remove('hidden');
      } else {
        etaEl.classList.add('hidden');
      }
    }
    
    // Предупреждение
    if (warningEl) {
      if (warning) {
        warningEl.textContent = warning;
        warningEl.classList.remove('hidden');
      } else {
        warningEl.classList.add('hidden');
      }
    }
  }

  /**
   * Скрывает блокирующий overlay. Минимум 5 с — только при первой загрузке приложения (isInitialLoad).
   * При загрузке модели/проекта и т.п. скрывается сразу по завершении.
   */
  public hideLoadingOverlay(): void {
    if (this.loadingOverlayIsInitial && this.loadingOverlayShownAt != null) {
      const elapsed = Date.now() - this.loadingOverlayShownAt;
      if (elapsed < LOADING_OVERLAY_MIN_DISPLAY_MS) {
        setTimeout(() => this.hideLoadingOverlay(), LOADING_OVERLAY_MIN_DISPLAY_MS - elapsed);
        return;
      }
      this.loadingOverlayIsInitial = false;
    }
    this.loadingOverlayShownAt = null;

    const overlay = document.getElementById('loading-overlay');
    const textEl = document.getElementById('loading-overlay-text');
    const progressEl = document.getElementById('loading-overlay-progress');
    const etaEl = document.getElementById('loading-overlay-eta');
    const warningEl = document.getElementById('loading-overlay-warning');
    
    if (overlay) {
      const FADE_DURATION_MS = 550;
      overlay.classList.add('loading-overlay-fade-out');
      const finishHide = () => {
        overlay.classList.add('hidden');
        overlay.classList.remove('loading-overlay-fade-out');
        this.loadingOverlayHideTimeout = null;
      };
      const onFadeEnd = (e: Event) => {
        const ev = e as AnimationEvent;
        if (ev.target !== overlay || ev.animationName !== 'loadingOverlayFadeOut') return;
        overlay.removeEventListener('animationend', onFadeEnd);
        finishHide();
      };
      overlay.addEventListener('animationend', onFadeEnd, { once: true });
      this.loadingOverlayHideTimeout = setTimeout(() => {
        this.loadingOverlayHideTimeout = null;
        if (!overlay.classList.contains('hidden')) finishHide();
      }, FADE_DURATION_MS);
    }
    
    if (textEl) {
      textEl.textContent = 'Загрузка…';
    }
    
    if (progressEl) {
      progressEl.textContent = '';
    }
    
    if (etaEl) {
      etaEl.classList.add('hidden');
      etaEl.textContent = '';
    }
    
    if (warningEl) {
      warningEl.classList.add('hidden');
      warningEl.textContent = '';
    }
  }

  /**
   * Показывает toast-уведомление с прогрессом
   */
  public showLoadingToast(text: string, progress?: number, id?: string): string {
    const toastId = id || `toast-${Date.now()}`;
    let toast = document.getElementById(toastId) as HTMLElement;
    
    if (!toast) {
      toast = document.createElement('div');
      toast.id = toastId;
      toast.className = 'loading-toast';
      toast.innerHTML = `
        <div class="loading-toast-content">
          <div class="loading-toast-spinner"></div>
          <div class="loading-toast-text">${text}</div>
          ${progress !== undefined ? `<div class="loading-toast-progress">${Math.round(progress)}%</div>` : ''}
        </div>
      `;
      document.body.appendChild(toast);
      
      // Анимация появления
      requestAnimationFrame(() => {
        toast.classList.add('show');
      });
    } else {
      // Обновляем существующий toast
      const textEl = toast.querySelector('.loading-toast-text');
      const progressEl = toast.querySelector('.loading-toast-progress');
      if (textEl) textEl.textContent = text;
      if (progressEl && progress !== undefined) {
        progressEl.textContent = `${Math.round(progress)}%`;
      }
    }
    
    return toastId;
  }
  
  /**
   * Скрывает toast-уведомление
   */
  public hideLoadingToast(id: string): void {
    const toast = document.getElementById(id);
    if (toast) {
      toast.classList.remove('show');
      setTimeout(() => {
        toast.remove();
      }, 300);
    }
  }
  
  /**
   * Обновляет прогресс в toast
   */
  public updateLoadingToast(id: string, text?: string, progress?: number): void {
    const toast = document.getElementById(id);
    if (!toast) return;
    
    const textEl = toast.querySelector('.loading-toast-text');
    const progressEl = toast.querySelector('.loading-toast-progress');
    
    if (text && textEl) textEl.textContent = text;
    if (progress !== undefined && progressEl) {
      progressEl.textContent = `${Math.round(progress)}%`;
    }
  }

  private initializeCustomDropdowns(): void {
    // Инициализируем dropdown для env-select
    this.initCustomDropdown('env-select', (value) => {
      const select = document.getElementById('env-select') as HTMLSelectElement;
      if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Инициализируем dropdown для aspect-ratio-select
    this.initCustomDropdown('aspect-ratio-select', (value) => {
      const select = document.getElementById('aspect-ratio-select') as HTMLSelectElement;
      if (select) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  private initCustomDropdown(selectId: string, onSelect: (value: string) => void): void {
    const wrapper = document.getElementById(`${selectId}-wrapper`);
    const button = document.getElementById(`${selectId}-button`);
    const menu = document.getElementById(`${selectId}-menu`);
    const text = document.getElementById(`${selectId}-text`);
    const select = document.getElementById(selectId) as HTMLSelectElement;

    if (!wrapper || !button || !menu || !text || !select) return;

    // Обновляем текст кнопки при загрузке
    this.updateCustomDropdown(selectId, select.value);

    // Обработчик клика по кнопке
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = !menu.classList.contains('hidden');
      
      // Закрываем все другие dropdown
      document.querySelectorAll('.custom-dropdown-menu').forEach(m => {
        if (m !== menu) m.classList.add('hidden');
      });
      document.querySelectorAll('.custom-dropdown').forEach(d => {
        if (d !== wrapper) d.classList.remove('open');
      });

      // Переключаем текущий dropdown
      if (isOpen) {
        menu.classList.add('hidden');
        wrapper.classList.remove('open');
      } else {
        menu.classList.remove('hidden');
        wrapper.classList.add('open');
      }
    });

    // Обработчик клика по элементам меню
    menu.addEventListener('click', (e: MouseEvent) => {
      const item = (e.target as HTMLElement).closest('.custom-dropdown-item');
      if (!item) return;

      const value = item.getAttribute('data-value');
      if (!value) return;

      // Показываем spinner для HDRI (только для env-select)
      if (selectId === 'env-select' && value !== 'none') {
        showCursorSpinner(e.clientX, e.clientY);
        
        // Скрываем spinner через небольшое время (будет скрыт когда HDRI загрузится)
        window.addEventListener('hdriLoadComplete', () => {
          hideCursorSpinner();
        }, { once: true });
      }

      // Обновляем выбранный элемент
      menu.querySelectorAll('.custom-dropdown-item').forEach(i => {
        i.classList.remove('selected');
      });
      item.classList.add('selected');

      // Обновляем текст кнопки (используем перевод, если есть data-i18n)
      const span = item.querySelector('span');
      if (span) {
        const i18nKey = span.getAttribute('data-i18n');
        if (i18nKey) {
          text.textContent = t(i18nKey);
          // Сохраняем data-i18n на кнопке для последующих обновлений языка
          if (text instanceof HTMLElement) {
            text.setAttribute('data-i18n', i18nKey);
          }
        } else {
          text.textContent = span.textContent || '';
        }
      } else {
        text.textContent = item.textContent || '';
      }
      
      // Обновляем иконку в кнопке для env-select
      if (selectId === 'env-select') {
        const iconElement = button.querySelector('.custom-dropdown-icon');
        if (iconElement) {
          iconElement.innerHTML = '';
          this.populateEnvironmentIcon(iconElement as SVGElement, value);
        }
      }
      
      // Обновляем скрытый select
      select.value = value;
      
      // Вызываем callback
      onSelect(value);

      // Закрываем меню
      menu.classList.add('hidden');
      wrapper.classList.remove('open');
    });

    // Закрываем при клике вне dropdown
    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target as Node)) {
        menu.classList.add('hidden');
        wrapper.classList.remove('open');
      }
    });
  }

  private updateCustomDropdown(selectId: string, value: string): void {
    const text = document.getElementById(`${selectId}-text`);
    const select = document.getElementById(selectId) as HTMLSelectElement;
    const menu = document.getElementById(`${selectId}-menu`);
    const button = document.getElementById(`${selectId}-button`);

    if (!text || !select) return;

    // Находим выбранную опцию
    const option = Array.from(select.options).find(opt => opt.value === value);
    if (option) {
      // Используем перевод, если есть data-i18n
      const i18nKey = option.getAttribute('data-i18n');
      if (i18nKey) {
        text.textContent = t(i18nKey);
      } else {
        text.textContent = option.textContent || '';
      }
      
      // Обновляем иконку в кнопке для env-select
      if (selectId === 'env-select' && button) {
        const iconElement = button.querySelector('.custom-dropdown-icon');
        if (iconElement) {
          // Очищаем старую иконку
          iconElement.innerHTML = '';
          // Создаем новую иконку для текущего окружения
          this.populateEnvironmentIcon(iconElement as SVGElement, value);
        }
      }
      
      // Обновляем визуальное выделение в меню
      if (menu) {
        menu.querySelectorAll('.custom-dropdown-item').forEach(item => {
          item.classList.remove('selected');
          if (item.getAttribute('data-value') === value) {
            item.classList.add('selected');
            // Обновляем текст в меню при обновлении
            const span = item.querySelector('span[data-i18n]');
            if (span) {
              const itemI18nKey = span.getAttribute('data-i18n');
              if (itemI18nKey) {
                span.textContent = t(itemI18nKey);
              }
            }
          }
        });
      }
    }
  }

  private populateHDRIOptions(select: HTMLSelectElement): void {
    // Очищаем и пересобираем список HDRI
    select.innerHTML = '';

    // Порядок окружений: сначала все HDRI из HDRI_MAPS, потом "Без окружения"
    const keys = Object.keys(HDRI_MAPS);
    const hasNight = keys.includes('night');

    // Добавляем все HDRI из HDRI_MAPS в исходном порядке
    keys.forEach((key, index) => {
      const option = document.createElement('option');
      option.value = key;
      const i18nKey = `panel.scene.environment.${key}`;
      option.setAttribute('data-i18n', i18nKey);
      // Используем перевод, если есть, иначе форматируем ключ
      option.textContent = t(i18nKey) !== i18nKey 
        ? t(i18nKey)
        : key.replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
      
      // Первый элемент по умолчанию
      if (index === 0) {
        option.selected = true;
      }
      
      select.appendChild(option);
    });

    // Добавляем опцию "Без окружения" последней
    const noneOption = document.createElement('option');
    noneOption.value = 'none';
    noneOption.setAttribute('data-i18n', 'panel.scene.environment.none');
    noneOption.textContent = t('panel.scene.environment.none');
    select.appendChild(noneOption);

    // Обновляем кастомный dropdown
    const menu = document.getElementById('env-select-menu');
    if (menu) {
      menu.innerHTML = '';
      Array.from(select.options).forEach(option => {
        const item = document.createElement('div');
        item.className = 'custom-dropdown-item';
        item.setAttribute('data-value', option.value);
        
        // Добавляем иконку окружения
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('class', 'custom-dropdown-item-icon');
        icon.setAttribute('width', '14');
        icon.setAttribute('height', '14');
        icon.setAttribute('viewBox', '0 0 24 24');
        icon.setAttribute('fill', 'none');
        icon.setAttribute('stroke', 'currentColor');
        icon.setAttribute('stroke-width', '2');
        
        // Используем общую функцию для создания иконки окружения
        this.populateEnvironmentIcon(icon, option.value);
        
        const span = document.createElement('span');
        span.textContent = option.textContent || '';
        
        item.appendChild(icon);
        item.appendChild(span);
        
        if (option.selected) {
          item.classList.add('selected');
        }
        menu.appendChild(item);
      });
      
      // Обновляем текст кнопки
      this.updateCustomDropdown('env-select', select.value);
    }
  }
}
